/** @jsx jsx */
import { jsx, Flex } from 'theme-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import RichText from './RichText';
import Button from './Button';
import Link from './Link';
import { InfoIcon, CloseIcon, TicketIcon, TicketSeriesIcon } from './Icon';

export default ({ title, products = [] }) => {
  const cards = useMemo(
    () => products.filter((card) => card && (!card.expires || moment().isBefore(card.expires))),
    [products]
  );

  if (!cards.length) {
    return null;
  }

  return (
    <Flex sx={{ flexDirection: 'column', width: '100%', gap: 3, mb: 4 }}>
      {title && <h2 sx={{ m: 0 }}>{title}</h2>}
      {cards.map((card, i) => (
        <TicketProductCard {...card} key={card?.contentful_id || i} />
      ))}
    </Flex>
  );
};

const TicketProductCard = ({
  ribbon,
  ribbonColor,
  icon,
  leftContentTitle,
  leftContentSubtitle,
  rightContent,
  rightContentSubtitle,
  infoContent,
  ctaButton,
  link,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const dialogRef = useRef(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const hasInfo = !!infoContent?.raw;

  useEffect(() => {
    if (!dialogRef.current) return;
    if (isInfoOpen) {
      document.body.style.overflow = 'hidden';
      dialogRef.current.showModal();
    } else {
      document.body.style.overflow = '';
      dialogRef.current.close();
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isInfoOpen]);

  return (
    <Flex
      sx={{
        borderRadius: 2,
        border: '2px solid',
        borderColor: 'border',
        flexDirection: 'column',
        width: '100%',
        maxWidth: [null, null, null, 750],
        position: 'relative',
      }}
    >
      {ribbon && (
        <Flex
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          <Label color={ribbonColor} text={ribbon} />
        </Flex>
      )}

      {hasInfo && (
        <button
          type="button"
          onClick={() => setIsInfoOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={isInfoOpen}
          aria-label={t('readMore')}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            m: 2,
            p: 0,
            border: 'none',
            bg: 'transparent',
            color: 'primary',
            cursor: 'pointer',
            lineHeight: 0,
          }}
        >
          <InfoIcon width="20" height="20" />
        </button>
      )}

      <Flex
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
          pl: [2, null, 5],
          py: ribbon || hasInfo ? ['36px', null, '40px'] : [4, null, '23px'],
          px: 3,
        }}
      >
        <Flex
          sx={{
            flex: [1, null, 1.8],
            minWidth: 0,
            alignItems: 'center',
            order: [1, null, 0],
            gap: [3, 4, 0],
          }}
        >
          <Flex
            sx={{
              flex: ['none', null, 1],
              justifyContent: ['flex-start', null, 'center'],
              alignItems: 'center',
              order: [0, null, 1],
              ml: [2, null, 0],
              minWidth: 40,
            }}
          >
            {icon === 'round-trip' ? (
              <TicketSeriesIcon width="40" height="40" aria-hidden="true" />
            ) : (
              <TicketIcon width="40" height="40" aria-hidden="true" />
            )}
          </Flex>

          <Flex
            sx={{
              flex: [1, null, 0.8],
              minWidth: 0,
              flexDirection: 'column',
              order: [1, null, 0],
              alignItems: 'flex-start',
            }}
          >
            {leftContentTitle && (
              <span
                sx={{
                  width: '100%',
                  fontSize: 18,
                  fontWeight: 'medium',
                  hyphens: 'auto',
                  overflowWrap: 'anywhere',
                  textAlign: 'left',
                }}
              >
                {leftContentTitle}
              </span>
            )}
            {leftContentSubtitle && (
              <span sx={{ width: '100%', fontSize: 1, color: 'black', overflowWrap: 'anywhere', textAlign: 'left' }}>
                {leftContentSubtitle}
              </span>
            )}
            {rightContent && (
              <Flex
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  display: ['flex', null, 'none'],
                  mt: leftContentSubtitle ? 2 : 1,
                }}
              >
                {link ? (
                  <Link
                    to={link}
                    noTitleFix
                    sx={{ textDecoration: 'none', color: 'primary', ':hover': { textDecoration: 'underline' } }}
                  >
                    <span sx={{ textAlign: 'left', fontSize: 16, fontWeight: 'semibold' }}>{rightContent}</span>
                  </Link>
                ) : (
                  <span sx={{ textAlign: 'left', fontSize: 16, fontWeight: 'semibold' }}>{rightContent}</span>
                )}
                {rightContentSubtitle && (
                  <span sx={{ fontSize: 1, color: 'black', textAlign: 'left' }}>{rightContentSubtitle}</span>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>

        {rightContent && (
          <Flex
            sx={{
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              display: ['none', null, 'flex'],
            }}
          >
            {link ? (
              <Link
                to={link}
                noTitleFix
                sx={{ textDecoration: 'none', color: 'primary', ':hover': { textDecoration: 'underline' } }}
              >
                <span sx={{ textAlign: 'left', fontSize: 18, fontWeight: 'medium' }}>{rightContent}</span>
              </Link>
            ) : (
              <span sx={{ textAlign: 'left', fontSize: 18, fontWeight: 'medium' }}>{rightContent}</span>
            )}
            {rightContentSubtitle && (
              <span sx={{ fontSize: 1, color: 'black', textAlign: 'left' }}>{rightContentSubtitle}</span>
            )}
          </Flex>
        )}

        {ctaButton && link && (
          <Flex sx={{ flex: ['0 0 auto', null, 1], flexShrink: 0, justifyContent: 'flex-end', order: [3, null, 0] }}>
            <Button
              as={Link}
              variant="small"
              to={link}
              noTitleFix
              sx={{ flexShrink: 0, whiteSpace: 'nowrap', textAlign: 'center' }}
            >
              {ctaButton}
            </Button>
          </Flex>
        )}
      </Flex>

      {hasInfo && (
        <dialog
          ref={dialogRef}
          role="dialog"
          aria-modal
          aria-label={t('readMore')}
          onClose={() => setIsInfoOpen(false)}
          onClick={(e) => {
            if (e.target === dialogRef.current) setIsInfoOpen(false);
          }}
          sx={{
            width: '90%',
            maxWidth: 560,
            p: 4,
            border: 'none',
            borderRadius: 2,
            '::backdrop': { bg: 'rgba(0, 0, 0, 0.5)' },
          }}
        >
          <Flex sx={{ flexDirection: 'column' }}>
            <Flex sx={{ justifyContent: 'flex-end', mb: 2 }}>
              <button
                type="button"
                onClick={() => setIsInfoOpen(false)}
                aria-label={t('close')}
                sx={{
                  p: 0,
                  border: 'none',
                  bg: 'transparent',
                  color: 'text',
                  cursor: 'pointer',
                  lineHeight: 0,
                }}
              >
                <CloseIcon />
              </button>
            </Flex>
            <RichText content={infoContent} locale={locale} />
            <Flex sx={{ justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="small" onClick={() => setIsInfoOpen(false)} sx={{ minWidth: 110, textAlign: 'center' }}>
                {t('close')}
              </Button>
            </Flex>
          </Flex>
        </dialog>
      )}
    </Flex>
  );
};

const Label = ({ color, text }) => (
  <Flex
    sx={{
      bg: color || 'primary',
      color: 'white',
      borderRadius: 1,
      fontSize: '11px',
      mx: 1,
      mt: 1,
      py: 1,
      px: 2,
    }}
  >
    <span>{text}</span>
  </Flex>
);
